import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import FormContainer from 'components/LoanForm/FormContainer';
import FormNavigation from 'components/FormNavigation';
import Button from 'components/Button';
import NumberInput from 'components/NumberInput';

import { FlowComponentType } from 'routes/types';
import { setCustomGoals } from 'handlers/yourGoal';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalPriority } from 'enums/AICoach';
import { updateAICoachGoals } from 'thunks';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { toggleAIChat } from 'handlers/aiChat';

import { getMessageForInvalidFields, getMessageForRequiredFields } from 'utils/errors';
import { getParsedNumericValue } from 'utils/getParsedNumericValue';
import { UpdateGoalsMode } from 'api/AICoachGoalsApi';
import { resetAiCoachConversation } from 'handlers/aiCoachConversation';
import { hasPrimaryGoalCompleted } from 'utils/aiCoachUtils';

import { AICoachGoalsFlowState } from 'components/AICoach/Goals/Goals';

import styles from './MonthlySavingsAmount.module.scss';

enum FormFields {
  MonthlySavingsAmount = 'monthlySavingsAmount',
}

const FormFieldValidationLabels = Object.freeze({
  [FormFields.MonthlySavingsAmount]: 'Amount',
});

const MonthlySavingsAmount = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const { aiCoachGoals, isLoading } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);

  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const location = useLocation();
  const { isFromFinancialDashboard, goalsChanged } = (location.state as AICoachGoalsFlowState) ?? {};

  const effectiveGoals = hasPrimaryGoalCompleted(aiCoachGoals) ? [] : aiCoachGoals;
  const selectedGoal = effectiveGoals?.[0];
  const initialMonthlySavingsAmount = useRef(selectedGoal?.monthlySavingsAmount);

  const {
    formState: { errors, isValid },
    register,
    watch,
    setValue,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      monthlySavingsAmount: selectedGoal?.monthlySavingsAmount ? selectedGoal.monthlySavingsAmount : undefined,
    },
  });

  useEffect(() => {
    register(FormFields.MonthlySavingsAmount, {
      required: getMessageForRequiredFields(FormFieldValidationLabels[FormFields.MonthlySavingsAmount]),
      min: {
        message: getMessageForInvalidFields(FormFieldValidationLabels[FormFields.MonthlySavingsAmount]),
        value: 1,
      },
    });
  });

  const watcher = watch();

  const handleContinue = async () => {
    if (!selectedGoal) return;

    const monthlySavingsAmount = watcher[FormFields.MonthlySavingsAmount];
    const customGoals: AICoachGoalItem[] = [
      {
        ...selectedGoal,
        monthlySavingsAmount,
        priority: AICoachGoalPriority.Primary,
      },
    ];

    dispatch(setCustomGoals(customGoals));

    if (isFromFinancialDashboard && application?.id) {
      const amountChanged = initialMonthlySavingsAmount.current !== monthlySavingsAmount;

      if (goalsChanged || amountChanged) {
        await dispatchWithUnwrap(
          updateAICoachGoals({ applicationId: application.id, goals: customGoals, mode: UpdateGoalsMode.Replace }),
        );
        dispatch(resetAiCoachConversation());
        dispatch(toggleAIChat());
      }
      navigate(RoutePath.FinancialDashboard);
      return;
    }

    handleNext();
  };

  return (
    <div>
      <FormNavigation {...navigationInfo} title={undefined} />
      <FormContainer
        title="How much can you set aside each month?"
        subtitle="An estimate is fine. We can refine later."
      >
        <div className={styles.mainContainer}>
          <NumberInput
            label="Enter amount"
            prefix="$"
            placeholder="$"
            errorMessage={errors[FormFields.MonthlySavingsAmount]?.message}
            thousandSeparator
            name={FormFields.MonthlySavingsAmount}
            onChange={(event) => {
              const { value } = event.target;
              setValue(FormFields.MonthlySavingsAmount, getParsedNumericValue(value));
              trigger(FormFields.MonthlySavingsAmount);
            }}
            value={watcher[FormFields.MonthlySavingsAmount] ? `${watcher[FormFields.MonthlySavingsAmount]}` : undefined}
            inputMode="numeric"
            onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
          />

          <Button onClick={handleContinue} disabled={!isValid} isLoading={isLoading && !!application?.id}>
            Next
          </Button>
        </div>
      </FormContainer>
    </div>
  );
};

export default MonthlySavingsAmount;
